import { useState } from 'react';
import { getIconHorseUrl } from '@clareefai/utils';

export interface LogoSuggestion {
  url: string;
  hostname: string;
}

/**
 * Company-URL → logo lookup. Call `lookup(url)` (e.g. on the URL field's blur):
 * it derives an icon.horse logo for the hostname, preloads it, and surfaces a
 * suggestion only if the image actually loads. The logo is applied to the form
 * only when the user confirms via `apply()` — nothing changes silently.
 */
export function useLogoSuggestion(currentLogo: string, onApply: (logo: string) => void) {
  const [suggestion, setSuggestion] = useState<LogoSuggestion | null>(null);
  const [checking, setChecking] = useState(false);

  const lookup = (rawUrl: string) => {
    const url = rawUrl?.trim();
    setSuggestion(null);
    if (!url) return;

    let hostname = '';
    try {
      hostname = new URL(url).hostname;
    } catch {
      return; // not a valid URL — nothing to look up
    }

    const iconUrl = getIconHorseUrl(url);
    // Skip if we couldn't build one or it's already the applied logo
    if (!iconUrl || iconUrl === currentLogo) return;

    setChecking(true);
    const img = new Image();
    img.onload = () => {
      setChecking(false);
      setSuggestion({ url: iconUrl, hostname });
    };
    img.onerror = () => {
      setChecking(false);
      setSuggestion(null);
    };
    img.src = iconUrl;
  };

  const apply = () => {
    if (suggestion) onApply(suggestion.url);
    setSuggestion(null);
  };

  const dismiss = () => setSuggestion(null);

  return { suggestion, checking, lookup, apply, dismiss };
}
